import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Menu, X, Plane } from 'lucide-react';
import TrripLogo from '../../assets/Trrip_logo/trrip_logo.png';
import landingData from '../landingData.json';

const Navbar = ({
  isScrolled: externalIsScrolled,
  activeNavSection,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  scrollToSection
}) => {
  const [internalIsScrolled, setInternalIsScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const widthTransform = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  useEffect(() => {
    const handleScroll = () => {
      setInternalIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isScrolled = externalIsScrolled ?? internalIsScrolled;

  const handleMobileLinkClick = (e, id) => {
    if (setIsMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
    if (scrollToSection) {
      scrollToSection(e, id);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'py-3 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200'
          : 'py-5 bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
        {/* Logo Area (Left) */}
        <div 
          className="flex items-center cursor-pointer"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <img
            src={TrripLogo}
            alt="Trrip"
            className="h-14 md:h-16 object-contain"
          />
        </div>

        {/* Nav Links (Right Aligned) */}
        <div className={`hidden md:flex items-center gap-6 lg:gap-8 text-sm font-semibold tracking-wide ${isScrolled ? 'text-gray-800' : 'text-gray-700'}`}>
          {landingData.navLinks.map(link => (
            <button
              key={link.id}
              onClick={(e) => scrollToSection(e, link.id)}
              className={`transition-colors relative group border-none bg-transparent cursor-pointer py-1 ${
                activeNavSection === link.id ? 'text-[#067978]' : 'hover:text-[#067978]'
              }`}
            >
              {link.label}
              <span 
                className={`absolute -bottom-1 left-0 h-0.5 bg-[#067978] transition-all ${
                  activeNavSection === link.id ? 'w-full' : 'w-0 group-hover:w-full'
                }`}
              ></span>
            </button>
          ))}
        </div>

        {/* Mobile Toggle */}
        <div className="flex items-center gap-4 md:hidden">
          <button 
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-[#067978] hover:bg-slate-100/80 rounded-lg transition-colors cursor-pointer border-none flex items-center justify-center"
            aria-label="Toggle Navigation Menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* MOBILE MENU DROPDOWN (100% Solid White, High Z-Index, Closes on Click) */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-gray-200 px-5 py-4 space-y-1.5 shadow-2xl z-[100]">
          {landingData.navLinks.map(link => (
            <button
              key={link.id}
              onClick={(e) => handleMobileLinkClick(e, link.id)}
              className={`block w-full text-left text-base font-bold py-3 px-4 rounded-xl transition-all cursor-pointer border-none ${
                activeNavSection === link.id 
                  ? 'bg-[#E0F2F1] text-[#067978]' 
                  : 'text-gray-800 hover:bg-gray-50 hover:text-[#067978]'
              }`}
            >
              {link.label}
            </button>
          ))}
        </div>
      )}

      {/* Scroll Progress Bar with Gliding Flight Spearhead */}
      <div className="absolute bottom-0 left-0 right-0 h-[5px] bg-teal-100/20 overflow-visible pointer-events-none">
        <motion.div
          style={{ width: widthTransform }}
          className="h-full bg-[#067978] relative overflow-visible shadow-[0_0_10px_rgba(6,121,120,0.6)]"
        >
          {/* The Gliding Plane Circle Node */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-30 flex items-center justify-center bg-white border-2 border-[#067978] rounded-full w-6 h-6 shadow-[0_2px_8px_rgba(6,121,120,0.4)] hover:scale-110 transition-all duration-200">
            <Plane size={13} className="text-[#067978] rotate-90" />
          </div>
        </motion.div>
      </div>
    </nav>
  );
};

export default Navbar;
